/**
=========================================================
* Material Dashboard 2  React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { use, useEffect, useMemo, useState } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import { Radio, RadioGroup, FormControlLabel, FormControl, CircularProgress } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// DefaultDoughnutChart configurations
import configs from "examples/Charts/DoughnutCharts/DefaultDoughnutChart/configs";
import { useTranslation } from "react-i18next";

ChartJS.register(ArcElement, Tooltip, Legend);

function DefaultDoughnutChart({ height, chart }) {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState();
  const [title, setTitle] = useState(t("admin.dashboard.donutPerDep"));
  const [value, setValue] = useState(0);

  const handleDataChange = (event) => {
    const valuee = event.target.value;
    setChartData(chart[valuee]);
    setValue(valuee);
    if (valuee == 0) setTitle(t("admin.dashboard.donutPerDep"));
    if (valuee == 1) setTitle(t("admin.dashboard.donutPerJob"));
    if (valuee == 2) setTitle(t("admin.dashboard.donutPerBranch"));
  };

  useEffect(() => {
    setChartData(chart[0]);
  }, [chart]);
  const renderChart = (
    <MDBox height="100%" display="flex" flexDirection="column" p={2}>
      {" "}
      {/* Title */}
      <MDBox mb={2}>
        <MDTypography variant="h6" align="center">
          {title}
        </MDTypography>
      </MDBox>
      {/* Radio buttons in a single row */}
      <MDBox display="flex" justifyContent="center" alignItems="center" mb={2}>
        <FormControl component="fieldset">
          <RadioGroup
            value={value}
            onChange={handleDataChange}
            row // Ensures the radio buttons are in a single row
          >
            <FormControlLabel
              value={0}
              // control={<Radio />}
              control={<Radio sx={{ transform: "scale(0.8)", p: 0.5 }} />}
              label={t("admin.dashboard.department")}
              sx={{ marginRight: 2 }}
            />
            <FormControlLabel
              value={1}
              control={<Radio sx={{ transform: "scale(0.8)", p: 0.5 }} />}
              label={t("admin.dashboard.job")}
              sx={{ marginRight: 2 }}
            />
            <FormControlLabel
              value={2}
              control={<Radio sx={{ transform: "scale(0.8)", p: 0.5 }} />}
              label={t("admin.dashboard.branch")}
            />
          </RadioGroup>
        </FormControl>
      </MDBox>
      {useMemo(() => {
        if (chartData) {
          const { data, options } = configs(
            chartData.labels || [],
            chartData.datasets || {},
            chartData.cutout
          );
          return (
            // <MDBox display="flex" justifyContent="center" alignItems="center">
            //   <Doughnut data={data} options={{ ...options, maintainAspectRatio: false }} />
            // </MDBox>
            <MDBox display="flex" justifyContent="center" alignItems="center">
              <MDBox
                width={{
                  xs: 200, // mobile
                  sm: 200, // small tablet
                  md: 160, // tablet
                  lg: 160, // desktop
                }}
                height={{
                  xs: 200,
                  sm: 200,
                  md: 160,
                  lg: 160,
                }}
              >
                <Doughnut data={data} options={{ ...options, maintainAspectRatio: false }} />
              </MDBox>
            </MDBox>
          );
        }
      }, [chart, chartData])}
    </MDBox>
  );

  if (chart.length < 1) {
    return (
      <MDBox height="100%" display="flex" justifyContent="center" alignItems="center">
        <CircularProgress size={24} />
      </MDBox>
    );
  }

  return renderChart;
}

// Setting default values for the props of DefaultDoughnutChart
DefaultDoughnutChart.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
  height: "19.125rem",
};

// Typechecking props for the DefaultDoughnutChart
DefaultDoughnutChart.propTypes = {
  icon: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
    ]),
    component: PropTypes.node,
  }),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
};

export default DefaultDoughnutChart;
